import { useCallback, useEffect, useRef } from 'react';
import { fetchQuery, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';

import { useAgentSessionsContext } from './AgentSessionsContext.ts';
import { AGENT_MENTION_COMMENT_SAVED_EVENT } from './agentMentionConstants.ts';
import { CommentAgentSessionsQuery } from './CommentAgentSessionsQuery.graphql.ts';
import type { CommentAgentSessionsQuery as CommentAgentSessionsQueryType } from './__generated__/CommentAgentSessionsQuery.graphql.ts';

// After an agent-mention comment is saved, the backend creates the SessionAssociationPublic
// asynchronously (once it has processed the chat-new trigger). We refetch the sessions query
// a few times at increasing offsets so the new comment's session connects without a page
// reload, regardless of how long the backend takes within this window.
export const SESSION_REFETCH_DELAYS_MS: number[] = [2000, 5000, 10000];

// The Content query defaults to current and archived pages when status is omitted. Include drafts
// explicitly so agent session status is also available while editing an unpublished page.
const CONTENT_STATUSES = ['current', 'draft', 'archived'];

// ConversationState mirrors the BE AgentSessionPublicState enum.
// Defined inline here to avoid importing from useCommentAgentSession (PR 2).
export type ConversationState = 'WORKING' | 'ARTEFACT_READY' | 'INPUT_REQUIRED' | 'FAILED';

const VALID_CONVERSATION_STATES = new Set<string>([
	'WORKING',
	'ARTEFACT_READY',
	'INPUT_REQUIRED',
	'FAILED',
]);

// Terminal snapshot state the backend reports once the agent has finished its task (the
// ConvoAiAgentSessionState `COMPLETED` value). Per design, a completed session shows NO
// agent-status row, so buildSessionMap skips these sessions entirely (see below).
const COMPLETED_STATE = 'COMPLETED';

export type CommentAgentSessionMap = Map<
	string, // numeric comment ID extracted from subEntityId ARI
	{
		conversationId: string;
		state: ConversationState | null;
		/**
		 * Atlassian identity account id (aaid) of the mentioned agent, or null when absent.
		 * Drives the agent avatar (and its deterministic fallback) when no picture URL is present.
		 * Always set by buildSessionMap (null when the session carries no agent), so non-optional.
		 */
		agentId: string | null;
		/** Display name of the mentioned agent (or null) — used as the avatar's alt/aria text. */
		agentName: string | null;
		/** Absolute URL of the mentioned agent's avatar (picture), or null when unavailable. */
		avatarUrl: string | null;
	}
>;

type PageCommentAgentSessionsOptions = {
	refetchOnMount?: boolean;
};

/**
 * Builds a CommentAgentSessionMap from a Relay query result.
 * Pure data transformation — no hooks, no side effects.
 */
function buildSessionMap(data: CommentAgentSessionsQueryType['response']): CommentAgentSessionMap {
	const sessionMap: CommentAgentSessionMap = new Map();
	// Both loops are bounded: content.nodes is always a single page node, and
	// allCommentAgentSessions is bounded by the number of agent-mentioned comments
	// on the page — a small, user-driven number in practice.
	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
	for (const node of data?.content?.nodes ?? []) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		for (const edge of node?.allCommentAgentSessions?.edges ?? []) {
			const session = edge?.node;
			if (session?.conversationId && session?.subEntityId) {
				// subEntityId format: "ari:cloud:confluence:<cloudId>:comment/<commentId>"
				// Extract the numeric comment ID from the end so map keys match comment.id
				// used in ObjectComment / ObjectCommentThread.
				const lastSlash = session.subEntityId.lastIndexOf('/');
				const commentId =
					lastSlash >= 0 ? session.subEntityId.slice(lastSlash + 1) : session.subEntityId;
				const rawState = session.agentSession?.state;
				// The agent has finished its task (COMPLETED): render no status row at all. Skip the
				// session entirely — keeping it with a null state would fall back to WORKING in
				// useCommentAgentSession and leave a stray connector branch. COMPLETED arrives via
				// this snapshot once the backend transitions the session terminal (after the agent's
				// planned action runs); the live subscription union has no terminal "done" member.
				if (rawState === COMPLETED_STATE) {
					continue;
				}
				const agent = session.agentSession?.agent;
				sessionMap.set(commentId, {
					conversationId: session.conversationId,
					state:
						rawState != null && VALID_CONVERSATION_STATES.has(rawState)
							? (rawState as ConversationState)
							: null,
					agentId: agent?.id ?? null,
					agentName: agent?.name ?? null,
					avatarUrl: agent?.picture ?? null,
				});
			}
		}
	}
	return sessionMap;
}

/**
 * Inner hook — only called when the experiment IS enabled.
 * Calls useLazyLoadQuery unconditionally (Rules of Hooks compliant) and
 * writes the result to AgentSessionsContext via the Provider in ObjectCommentContainer.
 *
 * Do NOT call this hook directly — use usePageCommentAgentSessions instead.
 */
export function usePageCommentAgentSessionsInner(
	pageId: string,
	options: PageCommentAgentSessionsOptions = {},
): CommentAgentSessionMap {
	const { refetchOnMount = false } = options;
	const environment = useRelayEnvironment();
	const data = useLazyLoadQuery<CommentAgentSessionsQueryType>(CommentAgentSessionsQuery, {
		pageId,
		status: CONTENT_STATUSES,
	});

	// Close the loop on newly-mentioned agents: when an agent-mention comment is saved
	// (AGENT_MENTION_COMMENT_SAVED_EVENT, also used to open the CwR sidebar), refetch the
	// sessions into the Relay store so the new comment's session connects without a reload.
	// fetchQuery updates the store imperatively and useLazyLoadQuery re-reads it reactively,
	// so this never re-suspends or blanks the comment UI. We poll a few bounded times to
	// absorb the backend's async creation of the SessionAssociationPublic.
	const refetchTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
	const clearRefetchTimers = useCallback(() => {
		refetchTimersRef.current.forEach(clearTimeout);
		refetchTimersRef.current = [];
	}, []);
	const scheduleSessionRefetches = useCallback(() => {
		// Supersede any in-flight poll from a previous save so timers don't pile up.
		clearRefetchTimers();
		refetchTimersRef.current = SESSION_REFETCH_DELAYS_MS.map((delay) =>
			setTimeout(() => {
				fetchQuery<CommentAgentSessionsQueryType>(
					environment,
					CommentAgentSessionsQuery,
					{ pageId, status: CONTENT_STATUSES },
					{ fetchPolicy: 'network-only' },
					// Best-effort background refresh — swallow errors so a failed refetch
					// never surfaces; the next poll (or page reload) will recover.
				).subscribe({ error: () => {} });
			}, delay),
		);
	}, [clearRefetchTimers, environment, pageId]);
	useEffect(() => {
		if (!refetchOnMount) {
			return;
		}
		scheduleSessionRefetches();

		return clearRefetchTimers;
	}, [clearRefetchTimers, refetchOnMount, scheduleSessionRefetches]);

	useEffect(() => {
		const handleAgentMentionCommentSaved = () => scheduleSessionRefetches();

		window.addEventListener(AGENT_MENTION_COMMENT_SAVED_EVENT, handleAgentMentionCommentSaved);
		return () => {
			window.removeEventListener(AGENT_MENTION_COMMENT_SAVED_EVENT, handleAgentMentionCommentSaved);
			clearRefetchTimers();
		};
	}, [clearRefetchTimers, scheduleSessionRefetches]);

	return buildSessionMap(data);
}

/**
 * Page-level hook that reads agent sessions for a given page.
 *
 * When the experiment is OFF: returns an empty Map immediately — no query, no Suspense.
 * When the experiment is ON: reads from AgentSessionsContext, which is populated by
 * AgentSessionsFetcher (mounted in ObjectCommentContainer only when experiment is on).
 *
 * Mount AgentSessionsFetcher at page level so the data is fetched once per page load
 * and shared across all comment instances via context.
 *
 * @returns A Map<commentId, { conversationId, state }>, or an empty Map if the
 *          experiment is off or no sessions exist.
 */
export function usePageCommentAgentSessions(): CommentAgentSessionMap {
	// When experiment is off, return immediately — zero queries, zero Suspense.
	// AgentSessionsFetcher (the component that calls useLazyLoadQuery) is never
	// mounted in ObjectCommentContainer when the gate is off, so this context
	// will always be the default empty Map in that case.
	return useAgentSessionsContext();
}
